<template>
    <div class="space-y-8">
        <div class="flex gap-4">
            <div class="w-1/3">
                <h3>Empty Initial Value</h3>
                <DsAddressAutocompleteSearch
                    v-model="searchTerm1"
                    id="sample-empty"
                    @locationSelected="selectedLocation1 = $event"
                />
                <div class="mt-4 space-y-2">
                    <h4 class="font-semibold">Selected Location Data:</h4>
                    <div class="rounded-lg bg-ds-primary-50 p-3 text-sm">
                        <p><strong>Formatted Address:</strong> {{ selectedLocation1?.formattedAddress || '' }}</p>
                        <p><strong>Display Name:</strong> {{ selectedLocation1?.displayName?.text || '' }}</p>
                        <p><strong>Place ID:</strong> {{ selectedLocation1?.id || '' }}</p>
                        <p>
                            <strong>Coordinates:</strong>
                            {{
                                selectedLocation1?.location
                                    ? `${selectedLocation1.location.latitude}, ${selectedLocation1.location.longitude}`
                                    : ''
                            }}
                        </p>
                    </div>

                    <h4 class="font-semibold">Address Components:</h4>
                    <div class="space-y-1 rounded-lg bg-ds-primary-50 p-3 text-sm">
                        <p><strong>Street Number:</strong> {{ getAddressComponent('street_number', selectedLocation1) }}</p>
                        <p><strong>Route:</strong> {{ getAddressComponent('route', selectedLocation1) }}</p>
                        <p><strong>City:</strong> {{ getAddressComponent('locality', selectedLocation1) }}</p>
                        <p><strong>State:</strong> {{ getAddressComponent('administrative_area_level_1', selectedLocation1) }}</p>
                        <p><strong>ZIP Code:</strong> {{ getAddressComponent('postal_code', selectedLocation1) }}</p>
                        <p><strong>Country:</strong> {{ getAddressComponent('country', selectedLocation1) }}</p>
                    </div>
                </div>
            </div>

            <div class="w-1/3">
                <h3>Prefilled Initial Value</h3>
                <DsAddressAutocompleteSearch
                    v-model="searchTerm2"
                    id="sample-prefilled"
                    @locationSelected="selectedLocation2 = $event"
                />
                <div class="mt-4 space-y-2">
                    <h4 class="font-semibold">Selected Location Data:</h4>
                    <div class="rounded-lg bg-ds-primary-50 p-3 text-sm">
                        <p><strong>Formatted Address:</strong> {{ selectedLocation2?.formattedAddress || '' }}</p>
                        <p><strong>Display Name:</strong> {{ selectedLocation2?.displayName?.text || '' }}</p>
                        <p><strong>Place ID:</strong> {{ selectedLocation2?.id || '' }}</p>
                        <p>
                            <strong>Coordinates:</strong>
                            {{
                                selectedLocation2?.location
                                    ? `${selectedLocation2.location.latitude}, ${selectedLocation2.location.longitude}`
                                    : 'None'
                            }}
                        </p>
                    </div>

                    <h4 class="font-semibold">Address Components:</h4>
                    <div class="space-y-1 rounded-lg bg-ds-primary-50 p-3 text-sm">
                        <p><strong>Street Number:</strong> {{ getAddressComponent('street_number', selectedLocation2) }}</p>
                        <p><strong>Route:</strong> {{ getAddressComponent('route', selectedLocation2) }}</p>
                        <p><strong>City:</strong> {{ getAddressComponent('locality', selectedLocation2) }}</p>
                        <p><strong>State:</strong> {{ getAddressComponent('administrative_area_level_1', selectedLocation2) }}</p>
                        <p><strong>ZIP Code:</strong> {{ getAddressComponent('postal_code', selectedLocation2) }}</p>
                        <p><strong>Country:</strong> {{ getAddressComponent('country', selectedLocation2) }}</p>
                    </div>
                </div>
            </div>

            <div class="w-1/3">
                <h3>Disabled</h3>
                <DsAddressAutocompleteSearch
                    v-model="searchTermDisabled"
                    id="sample-disabled"
                    disabled
                />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { GooglePlaceDetailsResponse } from '@/components/design-system/form/input/DsAddressAutocomplete/address-autocomplete.types';
import DsAddressAutocompleteSearch from '@/components/design-system/form/input/DsAddressAutocomplete/DsAddressAutocompleteSearch.vue';
import { ref } from 'vue';

const DEFAULT_SEARCH_TERM = '100 Congress Ave, Austin, TX 78701';

const selectedLocation1 = ref<GooglePlaceDetailsResponse>();
const selectedLocation2 = ref<GooglePlaceDetailsResponse>();

const searchTerm1 = ref('');
const searchTerm2 = ref(DEFAULT_SEARCH_TERM);
const searchTermDisabled = ref(DEFAULT_SEARCH_TERM);

const getAddressComponent = (type: string, selectedLocation: GooglePlaceDetailsResponse | undefined): string => {
    if (!selectedLocation?.addressComponents) {
        return '';
    }

    const component = selectedLocation.addressComponents.find(comp => comp.types.includes(type));
    return component?.longText || 'None';
};
</script>
